import { buildManualEmailAssistantQueryContext } from "./EmailAssistantManualQueryContextModel";
import { buildEmailAssistantOwnerQueryContext } from "./EmailAssistantOwnerQueryContextModel";
import type {
  EmailAssistantDraftPrefillContext,
  EmailAssistantQueryContext,
  EmailAssistantRecipientRuleOption,
} from "./EmailAssistantQueryContextTypes";

export {
  buildManualPreviewSummary,
  buildManualPreviewWarnings,
} from "./EmailAssistantManualPreviewModel";

export const EMAIL_ASSISTANT_CONTEXT_QUERY_KEYS = [
  "eaRecipientEmail",
  "eaRecipientName",
  "eaCommunicationEmail",
  "eaSourceType",
  "eaSourceName",
  "eaContext",
  "eaAlert",
  "eaSafeSource",
  "eaPersonId",
  "eaCommunicationScope",
  "eaOrganisationId",
  "eaOrganisationIds",
  "eaPartnerId",
  "eaPartnerIds",
  "eaAlertId",
  "eaIssueReportId",
  "eaSelectedCount",
  "eaSelectedUserIds",
] as const;

const EMAIL_ASSISTANT_INITIAL_SUBJECT_MAX_LENGTH = 160;

export const normalizeEmailAssistantInitialSubject = (
  value: string | null | undefined,
): string => {
  const normalized = value?.trim() ?? "";

  if (normalized.length <= EMAIL_ASSISTANT_INITIAL_SUBJECT_MAX_LENGTH) {
    return normalized;
  }

  return `${normalized.slice(0, EMAIL_ASSISTANT_INITIAL_SUBJECT_MAX_LENGTH - 3)}...`;
};

export const resolveSelectedRecipientRule = (
  options: EmailAssistantRecipientRuleOption[],
  selectedRuleId: string | undefined,
): EmailAssistantRecipientRuleOption | null => {
  const selectedOption = options.find((option) => option.id === selectedRuleId);
  if (selectedOption) {
    return selectedOption;
  }
  return options.length > 0 ? options[0] : null;
};

export const applyEmailAssistantDraftPrefill = (
  queryContext: EmailAssistantQueryContext | null,
  draftPrefill: EmailAssistantDraftPrefillContext | null,
): EmailAssistantQueryContext | null => {
  if (!queryContext || !draftPrefill) {
    return queryContext;
  }

  const prefillContextSections =
    draftPrefill.contextSections && draftPrefill.contextSections.length > 0
      ? draftPrefill.contextSections
      : undefined;
  const initialSubject = normalizeEmailAssistantInitialSubject(
    draftPrefill.subject,
  );

  return {
    ...queryContext,
    ...(draftPrefill.presentationOrigin === "email_activity_recovery" &&
    queryContext.crmHandoff?.sourceKind === "crm_contact"
      ? { presentationOrigin: draftPrefill.presentationOrigin }
      : {}),
    ...(initialSubject ? { initialSubject } : {}),
    ...(prefillContextSections
      ? {
          prefillContextSections,
        }
      : {}),
  };
};

export const isManualProfileSourceKind = (
  sourceKind:
    | NonNullable<EmailAssistantQueryContext["manualJobContext"]>["sourceKind"]
    | undefined,
): boolean =>
  sourceKind === "organisation_profile" || sourceKind === "partner_profile";

export const isManualDashboardSourceKind = (
  sourceKind:
    | NonNullable<EmailAssistantQueryContext["manualJobContext"]>["sourceKind"]
    | undefined,
): boolean =>
  sourceKind === "users_dashboard" ||
  sourceKind === "organisations_dashboard" ||
  sourceKind === "partners_dashboard";

export const buildEmailAssistantQueryContext = (
  searchParams: URLSearchParams,
): EmailAssistantQueryContext | null => {
  const manualContext = buildManualEmailAssistantQueryContext(searchParams);
  if (manualContext) {
    return manualContext;
  }

  return buildEmailAssistantOwnerQueryContext(searchParams);
};
